import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Icon, Modal } from 'semantic-ui-react';
import axios from 'axios';
import produce from 'immer';
import Button from 'components/ui/Button';
import { TaxLinkInfo } from 'components/forms/TaxSearches/NJTaxSearch/types';
import colors from 'css/colors';
import { NjTaxLink } from './NjTaxLink';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
`;
const Close = styled.button`
  display: flex;
  justify-content: flex-end;
  padding: 15px;
  background-color: #fff;
  border: none;
  cursor: pointer;
`;
const ActualContent = styled.div`
  padding: 0 35px;
`;
const ButtonContainer = styled.div`
  display: flex;
  gap: 9px;
  margin-top: 40px;
  justify-content: flex-end;
  position: relative;
`;
const SavedModal = styled.div`
  border-radius: 5px;
  background-color: #f5f5f5;
  width: 270px;
  height: 48px;
  color: #ff867f;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0px;
  right: 0px;
`;
const Error = styled.div`
  color: ${colors.red};
  margin-top: 8px;
`;

interface IError {
  url: boolean;
  title: boolean;
}
interface PropTypes {
  open: boolean;
  onClose: any;
  municipalityId: number;
}

const NjTaxLinksModal = (props: PropTypes) => {
  const [njTaxInfoLinks, setNjTaxInfoLinks] = useState<TaxLinkInfo[]>([]);
  const [linkIdsToDelete, setLinkIdsToDelete] = useState<number[]>([]);
  const [loader, setLoader] = useState(false);
  const [savedPopup, setSavedPopup] = useState(false);
  const [errors, setErrors] = useState<IError>({
    url: false,
    title: false,
  });

  const initialTaxLinkInfo: TaxLinkInfo = {
    id: null,
    municipalityId: props.municipalityId,
    link: '',
    software: null,
    title: null,
  };

  useEffect(() => {
    if (props.municipalityId) {
      axios.get(`/api/municipalities/GetNjTaxLinks/${props.municipalityId}`).then(({ data }) => {
        if (data) {
          setNjTaxInfoLinks(data);
        }
      });
    }
  }, [props.municipalityId]);

  const onSubmit = async () => {
    const foundErrors = checkForErrors();
    if (!foundErrors) {
      setLoader(true);
      await axios.post(`/api/municipalities/UpdateInsertDeleteNjTaxLinks`, {
        njTaxInfoLinks,
        linkIdsToDelete,
      });
      setLoader(false);
      setSavedPopup(true);
      setTimeout(() => {
        setSavedPopup(false);
        props.onClose();
      }, 1000);
    }
  };

  const checkForErrors = () => {
    const newErrors = produce(errors, draft => {
      draft.url = njTaxInfoLinks.some(l => l.link === null || l.link === '');
      draft.title = njTaxInfoLinks.some(l => l.title === null || l.title === '');
    });

    setErrors(newErrors);
    return newErrors.url || newErrors.title;
  }

  const handleDelete = (idx: number, id: number) => {
    if (id) {
      const newIdsToDelete = produce(linkIdsToDelete, draft => {
        draft.push(id);
      });
      setLinkIdsToDelete(newIdsToDelete);
    }
    const newLinks = produce(njTaxInfoLinks, draft => {
      draft.splice(idx, 1);
    });
    setNjTaxInfoLinks(newLinks);
  };

  const handleInfoChange = (event: React.ChangeEvent<HTMLInputElement>, label: string, idx: number) => {
    const nextState = produce(njTaxInfoLinks, draft => {
      const thisOne = draft[idx];
      (thisOne as any)[label] = event.target.value;
    });
    setNjTaxInfoLinks(nextState);
  };

  return (
    <Modal
      open={props.open}
      style={{ minHeight: 275, width: 700, zIndex: 500, paddingBottom: 24 }}
      onClose={props.onClose}
    >
      <ModalContent>
        <Close onClick={props.onClose}>
          <Icon name="close" />
        </Close>
        <h1 style={{ margin: 'auto', marginBottom: 8 }}>
          <b>Edit Tax Links</b>
        </h1>
        <ActualContent>
          {njTaxInfoLinks?.map((l, idx) => {
            return (
              <NjTaxLink
                idx={idx}
                linkInfo={l}
                handleDelete={handleDelete}
                handleInfoChange={handleInfoChange}
              />
            );
          })}
          <div
            style={{ display: 'flex', color: 'green', cursor: 'pointer', margin: 20 }}
            onClick={() => {
              const next = produce(njTaxInfoLinks, draft => {
                draft.push(initialTaxLinkInfo);
              });
              setNjTaxInfoLinks(next);
            }}
          >
            Add Link
          </div>
          {errors.url && <Error>Please make sure all links have a valid URL</Error>}
          {errors.title && <Error>Please make sure all links have a title</Error>}

          <ButtonContainer>
            <Button secondary onClick={props.onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                onSubmit();
              }}
              loadingStatus={loader}
            >
              Save
            </Button>
            {savedPopup && <SavedModal>Successfully Saved</SavedModal>}
          </ButtonContainer>
        </ActualContent>
      </ModalContent>
    </Modal>
  );
};

export default NjTaxLinksModal;
